import { motion } from "framer-motion";
import { Zap, Wrench, Wallet, Banknote, ShieldCheck, Clock } from "lucide-react";

const WhyChooseUs = () => {
  const benefits = [
    {
      icon: Zap,
      title: "Fast Closings",
      description: "Close in as little as 7 days. We move at your pace, not the market's.",
    },
    {
      icon: Wrench,
      title: "No Repairs Needed",
      description: "Sell your house as-is. We buy homes in any condition—no fixes required.",
    },
    {
      icon: Wallet,
      title: "No Fees or Commissions",
      description: "Keep more of your money. We don't charge fees or realtor commissions.",
    },
    {
      icon: Banknote,
      title: "Cash Offers",
      description: "Get a fair, all-cash offer with no financing contingencies to worry about.",
    },
    {
      icon: ShieldCheck,
      title: "Transparent Process",
      description: "No hidden costs or surprises. We explain everything upfront and honestly.",
    },
    {
      icon: Clock,
      title: "Your Timeline",
      description: "Need more time? No problem. Choose a closing date that works for you.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <section id="why-us" className="py-24 md:py-32 bg-white" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-sm font-semibold text-[#D32F2F] uppercase tracking-wide mb-4">
            Our Advantages
          </span>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Why Choose JJbuyshouses
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            We make selling your home simple, fast, and stress-free. Here's what sets us apart.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group p-6 rounded-2xl bg-slate-50 hover:bg-white border border-transparent hover:border-slate-100 hover:shadow-lg transition-all duration-300"
              data-testid={`benefit-card-${index}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#D32F2F]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#D32F2F] transition-colors duration-300">
                  <benefit.icon className="w-6 h-6 text-[#D32F2F] group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <h3 
                    className="text-lg font-bold text-slate-900 mb-2"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
